<template>
  <div class="tdtjs-wrapper">
    <div class="tdtjs-map" ref="mapEl"></div>
    <div v-if="!ready" class="tdtjs-loading">正在加载天地图…</div>
    <div v-else-if="initError" class="tdtjs-error">
      天地图加载失败：{{ initError }}
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import { loadTiandituJsApi } from '../utils/loadTiandituJsApi'
import { buildProjectClusters, TIANDITU_JS_DETAIL_ZOOM_THRESHOLD } from '../utils/stationCluster'
import { dashboardStationPayload } from '../utils/dashboardNavigation.mjs'

let chinaGeoJsonCache = null
let isChinaGeoJsonLoaded = false
const MIN_OUT_ZOOM = 4

export default {
  name: 'TiandituJsSatelliteMapComponent',
  props: {
    stations: { type: Array, default: () => [] }
  },
  emits: ['station-click', 'province-click', 'cluster-click', 'mode-change', 'map-ready'],
  data() {
    return {
      ready: false,
      initError: '',
      map: null,
      T: null,
      selectedProvince: null,
      selectedStation: null,
      currentZoom: 6,
      provinceOverlays: [],
      markerOverlays: [],
      // Base64 encoded SVGs for markers (Premium "Jewel" design)
      icons: {
        normal: 'data:image/svg+xml;base64,' + btoa('<svg width="32" height="32" viewBox="0 0 32 32" xmlns="http://www.w3.org/2000/svg"><defs><radialGradient id="g1" cx="50%" cy="50%" r="50%"><stop offset="0%" stop-color="#fff"/><stop offset="30%" stop-color="#0ea5e9"/><stop offset="100%" stop-color="#023d54" stop-opacity="0.2"/></radialGradient></defs><circle cx="16" cy="16" r="14" fill="url(#g1)" stroke="#0ea5e9" stroke-width="0.5" /><circle cx="16" cy="16" r="4" fill="#fff" /></svg>'),
        warning: 'data:image/svg+xml;base64,' + btoa('<svg width="32" height="32" viewBox="0 0 32 32" xmlns="http://www.w3.org/2000/svg"><defs><radialGradient id="g2" cx="50%" cy="50%" r="50%"><stop offset="0%" stop-color="#fff"/><stop offset="30%" stop-color="#fbbf24"/><stop offset="100%" stop-color="#451a03" stop-opacity="0.2"/></radialGradient></defs><circle cx="16" cy="16" r="14" fill="url(#g2)" stroke="#fbbf24" stroke-width="0.5" /><circle cx="16" cy="16" r="4" fill="#fff" /></svg>'),
        error: 'data:image/svg+xml;base64,' + btoa('<svg width="32" height="32" viewBox="0 0 32 32" xmlns="http://www.w3.org/2000/svg"><defs><radialGradient id="g3" cx="50%" cy="50%" r="50%"><stop offset="0%" stop-color="#fff"/><stop offset="30%" stop-color="#f43f5e"/><stop offset="100%" stop-color="#4c0519" stop-opacity="0.2"/></radialGradient></defs><circle cx="16" cy="16" r="14" fill="url(#g3)" stroke="#f43f5e" stroke-width="0.5" /><circle cx="16" cy="16" r="4" fill="#fff" /></svg>'),
        cluster: 'data:image/svg+xml;base64,' + btoa('<svg width="48" height="48" viewBox="0 0 48 48" xmlns="http://www.w3.org/2000/svg"><circle cx="24" cy="24" r="22" fill="rgba(14, 165, 233, 0.15)" stroke="#0ea5e9" stroke-width="1.5" /><circle cx="24" cy="24" r="16" fill="rgba(14, 165, 233, 0.4)" stroke="#fff" stroke-width="1" stroke-dasharray="2,2"/><circle cx="24" cy="24" r="4" fill="#fff" /></svg>')
      },
      pendingViewState: null,
      /** Parent may call zoomToStation before initMap finishes (same pattern as pendingViewState). */
      pendingZoomStation: null
    }
  },
  computed: {
    displayStations() {
      if (this.currentZoom >= TIANDITU_JS_DETAIL_ZOOM_THRESHOLD) return this.stations || []
      return buildProjectClusters(this.stations || [])
    }
  },
  watch: {
    displayStations: {
      handler() {
        this.renderMarkers()
      }
    }
  },
  async mounted() {
    try {
      await this.loadChinaGeoJson()
      this.T = await loadTiandituJsApi({ tk: process.env.VUE_APP_TIANDITU_TK, v: '4.0' })
      await this.initMap()
      this.ready = true
      this.applyPendingViewState()
      this.flushPendingZoomStation()
      this.$emit('map-ready')
    } catch (e) {
      this.initError = String(e?.message || e)
      console.error('[TiandituJsSatelliteMapComponent] init failed:', e)
    }
  },
  beforeUnmount() {
    this.clearOverlays()
    this.map = null
  },
  methods: {
    syncViewState({ provinceName } = {}) {
      const nextProvince = provinceName || '全国'
      if (!this.ready || !this.map) {
        this.pendingViewState = { provinceName: nextProvince }
        return
      }
      this.applyProvinceSelection(nextProvince)
    },
    applyPendingViewState() {
      if (!this.pendingViewState) return
      const { provinceName } = this.pendingViewState
      this.pendingViewState = null
      this.applyProvinceSelection(provinceName || '全国')
    },
    flushPendingZoomStation() {
      if (!this.pendingZoomStation) return
      const s = this.pendingZoomStation
      this.pendingZoomStation = null
      this.zoomToStation(s)
    },
    applyProvinceSelection(provinceName) {
      const target = provinceName || '全国'
      const normalized = target === '全国' ? null : target
      const changed = this.selectedProvince !== normalized
      this.selectedProvince = normalized
      this.selectedStation = null
      this.renderProvinces()
      // Keep behavior idempotent: allow re-focus on mode switch even if same province.
      if (target === '全国') {
        this.resetMapView()
      } else {
        this.zoomToProvince(target)
      }
      if (!changed) return
    },
    async loadChinaGeoJson() {
      if (isChinaGeoJsonLoaded) return
      try {
        const res = await axios.get('/china.json')
        chinaGeoJsonCache = Object.freeze(res.data)
        isChinaGeoJsonLoaded = true
      } catch (err) {
        try {
          const res = await axios.get('/geofusion/china.json')
          chinaGeoJsonCache = Object.freeze(res.data)
          isChinaGeoJsonLoaded = true
        } catch (err2) {
          const res = await axios.get('https://geo.datav.aliyun.com/areas_v3/bound/100000_full.json')
          chinaGeoJsonCache = Object.freeze(res.data)
          isChinaGeoJsonLoaded = true
        }
      }
    },

    async initMap() {
      const el = this.$refs.mapEl
      if (!el || !this.T) throw new Error('map_dom_or_T_missing')

      const T = this.T
      // Map projection: use WebMercator (w) to match img_w/cia_w.
      this.map = new T.Map(el, { projection: 'EPSG:900913' })

      // Prefer official JS API map-type switch. This is more reliable than
      // manually constructing TileLayer URL templates in different SDK builds.
      if (window.TMAP_HYBRID_MAP) {
        this.map.setMapType(window.TMAP_HYBRID_MAP)
      } else if (window.TMAP_SATELLITE_MAP) {
        this.map.setMapType(window.TMAP_SATELLITE_MAP)
      } else {
        // Fallback for SDK variants without TMAP_* constants.
        const tk = (process.env.VUE_APP_TIANDITU_TK || '').trim()
        const isDev = process.env.NODE_ENV === 'development'
        const base = isDev ? '/tianditu-proxy/t0' : 'https://t0.tianditu.gov.cn'
        const imgUrl =
          `${base}/img_w/wmts?` +
          `SERVICE=WMTS&REQUEST=GetTile&VERSION=1.0.0&LAYER=img&STYLE=default&` +
          `TILEMATRIXSET=w&FORMAT=tiles&TILEMATRIX={z}&TILEROW={y}&TILECOL={x}&tk=${encodeURIComponent(tk)}`
        const ciaUrl =
          `${base}/cia_w/wmts?` +
          `SERVICE=WMTS&REQUEST=GetTile&VERSION=1.0.0&LAYER=cia&STYLE=default&` +
          `TILEMATRIXSET=w&FORMAT=tiles&TILEMATRIX={z}&TILEROW={y}&TILECOL={x}&tk=${encodeURIComponent(tk)}`
        const imgLayer = new T.TileLayer(imgUrl, { minZoom: 1, maxZoom: 18 })
        const ciaLayer = new T.TileLayer(ciaUrl, { minZoom: 1, maxZoom: 18 })
        this.map.addLayer(imgLayer)
        this.map.addLayer(ciaLayer)
      }

      // Default view: China
      this.map.centerAndZoom(new T.LngLat(104.114129, 37.550339), MIN_OUT_ZOOM)
      this.currentZoom = MIN_OUT_ZOOM

      this.map.addEventListener('zoomend', () => {
        try {
          const z = this.map.getZoom()
          // Clamp zoom-out to avoid world-repeat tiles in satellite mode.
          if (typeof z === 'number' && z < MIN_OUT_ZOOM) {
            const c = this.map.getCenter?.()
            const lng = c?.lng ?? 104.114129
            const lat = c?.lat ?? 37.550339
            this.map.centerAndZoom(new this.T.LngLat(lng, lat), MIN_OUT_ZOOM)
            this.currentZoom = MIN_OUT_ZOOM
            return
          }
          this.currentZoom = z
        } catch (e) {
          // ignore
        }
      })

      this.renderProvinces()
      this.renderMarkers()

      // Do not emit default province here.
      // Parent Dashboard is the single source of truth and will sync state in.
    },

    clearOverlays() {
      if (!this.map) return
      const arr = [...(this.provinceOverlays || []), ...(this.markerOverlays || [])]
      arr.forEach((o) => {
        try {
          this.map.removeOverLay(o)
        } catch (e) {
          // ignore
        }
      })
      this.provinceOverlays = []
      this.markerOverlays = []
    },

    renderProvinces() {
      if (!this.map || !this.T || !chinaGeoJsonCache?.features) return

      // Remove old province overlays
      ;(this.provinceOverlays || []).forEach((o) => {
        try {
          this.map.removeOverLay(o)
        } catch (e) {
          // ignore
        }
      })
      this.provinceOverlays = []

      const T = this.T

      chinaGeoJsonCache.features.forEach((f) => {
        const name = f?.properties?.name
        if (!name) return
        const rings = this.flattenFeatureToRings(f)
        if (!rings || rings.length === 0) return

        // TianDiTu Polygon supports one ring via array of LngLat; for multipolygon we add multiple polygons.
        rings.forEach((ring) => {
          const lnglats = ring.map((p) => new T.LngLat(p[0], p[1]))
          const isSelected = this.selectedProvince === name
          const poly = new T.Polygon(lnglats, {
            color: isSelected ? '#00f0ff' : '#3b82f6',
            weight: isSelected ? 3 : 2,
            opacity: isSelected ? 0.95 : 0.55,
            fillColor: isSelected ? '#1e40af' : '#000000',
            fillOpacity: isSelected ? 0.12 : 0.01
          })
          poly.__provinceName = name
          poly.addEventListener('click', () => this.onProvinceClick(name, f))
          this.map.addOverLay(poly)
          this.provinceOverlays.push(poly)
        })
      })
    },

    onProvinceClick(name, feature) {
      if (!name) return
      if (this.selectedProvince === name) {
        this.selectedProvince = null
        this.selectedStation = null
        this.renderProvinces()
        this.resetMapView()
        this.$emit('province-click', { name: '全国' })
        return
      }

      this.selectedProvince = name
      this.selectedStation = null
      this.renderProvinces()
      const center = this.getFeatureCenter(feature)
      this.zoomToProvince(name)
      this.$emit('province-click', { name, center })
    },

    renderMarkers() {
      if (!this.map || !this.T) return

      // Remove old markers
      ;(this.markerOverlays || []).forEach((o) => {
        try {
          this.map.removeOverLay(o)
        } catch (e) {
          // ignore
        }
      })
      this.markerOverlays = []

      const T = this.T
      const items = this.displayStations || []
      items.forEach((s) => {
        if (!s?.value || s.value.length < 2) return
        const lng = s.value[0]
        const lat = s.value[1]
        const isCluster = Boolean(s.isCluster)

        const status = s?.status || 'normal'
        const iconUrl = isCluster ? this.icons.cluster : (this.icons[status] || this.icons.normal)
        const size = isCluster ? new T.Point(40, 40) : new T.Point(32, 32)
        
        const icon = new T.Icon({
          iconUrl: iconUrl,
          iconSize: size,
          iconAnchor: new T.Point(size.x / 2, size.y / 2)
        })

        const marker = new T.Marker(new T.LngLat(lng, lat), { icon })
        marker.__data = s
        marker.addEventListener('click', () => this.onMarkerClick(s))
        this.map.addOverLay(marker)
        this.markerOverlays.push(marker)

        // Label for clusters
        if (isCluster && s.name) {
          // Tianditu JS API builds differ; some builds don't expose T.Label.
          // Prefer marker.setLabel when available, otherwise skip label (but never crash).
          try {
            if (typeof marker.setLabel === 'function' && T.Label) {
              const label = new T.Label({
                text: s.name,
                position: new T.LngLat(lng, lat),
                offset: new T.Point(12, -10)
              })
              marker.setLabel(label)
            } else if (typeof marker.setTitle === 'function') {
              marker.setTitle(s.name)
            }
          } catch (e) {
            // ignore label failures
          }
        }
      })
    },

    onMarkerClick(s) {
      const isCluster = Boolean(s?.isCluster)
      if (isCluster) {
        if (s.projectId != null) this.$emit('cluster-click', { projectId: s.projectId, projectName: s.projectName })
        this.zoomToProjectCluster(s.children || [])
        return
      }

      const stationName = s?.name
      if (!stationName || !s?.value) return
      const lng = s.value[0]
      const lat = s.value[1]

      if (this.selectedStation === s.sid_no) {
        this.selectedStation = null
        this.zoomToProvince(this.selectedProvince || '全国')
        this.$emit('province-click', { name: this.selectedProvince || '全国' })
      } else {
        this.selectedStation = s.sid_no
        this.zoomToStation(s)
        this.$emit('station-click', dashboardStationPayload(s, lng, lat))
      }
    },

    // ===== Public methods (compatible with DashboardView ref calls) =====
    zoomToProvince(provinceName) {
      if (!this.map || !chinaGeoJsonCache?.features) return
      if (!provinceName || provinceName === '全国') {
        this.resetMapView()
        return
      }
      const feature = chinaGeoJsonCache.features.find((f) => f?.properties?.name === provinceName)
      if (!feature) return
      const center = this.getFeatureCenter(feature)
      if (!center) return
      // Pick a reasonable zoom; keep similar to ECharts province zoom range.
      this.map.centerAndZoom(new this.T.LngLat(center[0], center[1]), 7)
    },

    zoomToStation(station) {
      if (!station?.value) return
      if (!this.map || !this.T) {
        this.pendingZoomStation = station
        return
      }
      if (!this.ready) {
        this.pendingZoomStation = station
        return
      }
      const [lng, lat] = station.value
      // 必须高于「聚合/单点」分界，否则仍渲染项目 centroid 总点，看起来像未对准仪器、缩放不动
      const z = Math.min(18, Math.max(TIANDITU_JS_DETAIL_ZOOM_THRESHOLD + 1, 15))
      this.map.centerAndZoom(new this.T.LngLat(lng, lat), z)
      this.selectedStation = station.sid_no
    },

    zoomToProjectCluster(devices) {
      if (!this.map || !devices || devices.length === 0) return
      // Fit by center of bounds (simple); keep zoom lower so cluster view remains.
      let minX = Infinity,
        maxX = -Infinity,
        minY = Infinity,
        maxY = -Infinity
      devices.forEach((d) => {
        if (d?.value?.length >= 2) {
          const [lng, lat] = d.value
          minX = Math.min(minX, lng)
          maxX = Math.max(maxX, lng)
          minY = Math.min(minY, lat)
          maxY = Math.max(maxY, lat)
        }
      })
      if (!isFinite(minX)) return
      const center = [(minX + maxX) / 2, (minY + maxY) / 2]
      const span = Math.max(maxX - minX, maxY - minY)
      let targetZoom = 7
      if (span < 0.01) targetZoom = 12
      else if (span < 0.05) targetZoom = 11
      else if (span < 0.2) targetZoom = 10
      else if (span < 0.8) targetZoom = 9
      else if (span < 2) targetZoom = 8
      // 不超过「单点」分界，保持项目总点；用户再放大才展开（对齐 2D）
      targetZoom = Math.min(targetZoom, TIANDITU_JS_DETAIL_ZOOM_THRESHOLD - 1)
      this.map.centerAndZoom(new this.T.LngLat(center[0], center[1]), targetZoom)
    },

    resetMapView() {
      if (!this.map || !this.T) return
      this.map.centerAndZoom(new this.T.LngLat(104.114129, 37.550339), MIN_OUT_ZOOM)
    },

    // ===== GeoJSON helpers =====
    flattenFeatureToRings(feature) {
      const geom = feature?.geometry
      if (!geom) return []
      const t = geom.type
      const c = geom.coordinates

      // Return array of rings, each ring is array of [lng,lat]
      if (t === 'Polygon') {
        // c: [ring1, ring2...]
        return (c || []).slice(0, 1) // keep outer ring only for performance
      }
      if (t === 'MultiPolygon') {
        // c: [[ring1...],[ring1...],...]
        const rings = []
        ;(c || []).forEach((poly) => {
          if (Array.isArray(poly?.[0])) rings.push(poly[0]) // outer ring only
        })
        return rings
      }
      return []
    },

    getFeatureCenter(feature) {
      const coords = feature?.geometry?.coordinates
      if (!coords) return null
      let minX = Infinity,
        maxX = -Infinity,
        minY = Infinity,
        maxY = -Infinity
      let hasPoints = false

      const processCoords = (arr) => {
        if (typeof arr?.[0] === 'number') {
          hasPoints = true
          minX = Math.min(minX, arr[0])
          maxX = Math.max(maxX, arr[0])
          minY = Math.min(minY, arr[1])
          maxY = Math.max(maxY, arr[1])
        } else if (Array.isArray(arr)) {
          arr.forEach(processCoords)
        }
      }
      processCoords(coords)
      if (!hasPoints) return null
      return [(minX + maxX) / 2, (minY + maxY) / 2]
    }
  }
}
</script>

<style scoped>
.tdtjs-wrapper {
  position: relative;
  width: 100%;
  height: 100%;
  overflow: hidden;
}

.tdtjs-map {
  width: 100%;
  height: 100%;
}

.tdtjs-loading,
.tdtjs-error {
  position: absolute;
  top: 12px;
  left: 12px;
  z-index: 5;
  max-width: 420px;
  padding: 10px 12px;
  border-radius: 10px;
  background: rgba(15, 23, 42, 0.75);
  border: 1px solid rgba(0, 240, 255, 0.25);
  color: #fff;
  font-size: 12px;
  pointer-events: none;
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
}

.tdtjs-error {
  border-color: rgba(239, 68, 68, 0.5);
}
</style>

